<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';

import SectionLayout from './SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const { currentAccount } = useAccount();

const showDeleteConfirmation = ref(false);

const uiFlags = computed(() => store.getters['accounts/getUIFlags']);
const customAttributes = computed(
  () => currentAccount.value?.custom_attributes || {}
);
const markedForDeletionAt = computed(
  () => customAttributes.value.marked_for_deletion_at
);
const markedForDeletionReason = computed(
  () => customAttributes.value.marked_for_deletion_reason
);
const accountName = computed(() => currentAccount.value?.name || '');
const deletionDate = computed(() => {
  if (!markedForDeletionAt.value) return '';
  const parsed = new Date(markedForDeletionAt.value);
  if (Number.isNaN(parsed.getTime())) return markedForDeletionAt.value;
  return parsed.toLocaleDateString();
});
const scheduledMessage = computed(() => {
  const interpolations = { deletionDate: deletionDate.value };
  if (markedForDeletionReason.value === 'inactivity') {
    return t(
      'GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.SCHEDULED_DELETION.MESSAGE_INACTIVITY',
      interpolations
    );
  }
  return t(
    'GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.SCHEDULED_DELETION.MESSAGE_MANUAL',
    interpolations
  );
});

const openDeletePopup = () => {
  showDeleteConfirmation.value = true;
};
const closeDeletePopup = () => {
  showDeleteConfirmation.value = false;
};

const toggleDeletion = async actionType => {
  const cancelingInactivity =
    actionType === 'undelete' && markedForDeletionReason.value === 'inactivity';
  try {
    await store.dispatch('accounts/toggleDeletion', { actionType });
    if (actionType === 'delete') {
      useAlert(t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.SUCCESS'));
    } else if (cancelingInactivity) {
      useAlert(
        t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.SUCCESS_UNDELETE_INACTIVITY')
      );
    } else {
      useAlert(t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.SUCCESS_UNDELETE'));
    }
  } catch (error) {
    const oncePerDay =
      error?.response?.data?.error_type === 'deletion_already_requested_today';
    useAlert(
      t(
        oncePerDay
          ? 'GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.FAILURE_ONCE_PER_DAY'
          : 'GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.FAILURE'
      )
    );
  } finally {
    closeDeletePopup();
  }
};

const confirmDeletion = () => toggleDeletion('delete');
const cancelDeletion = () => toggleDeletion('undelete');
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.TITLE')"
    :description="t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.NOTE')"
    with-border
  >
    <div v-if="markedForDeletionAt" class="grid gap-4">
      <p class="text-sm text-n-slate-11">
        {{ scheduledMessage }}
      </p>
      <div>
        <NextButton
          slate
          faded
          type="button"
          :is-loading="uiFlags.isUpdating"
          @click="cancelDeletion"
        >
          {{
            t(
              'GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.SCHEDULED_DELETION.CLEAR_BUTTON'
            )
          }}
        </NextButton>
      </div>
    </div>
    <div v-else>
      <NextButton
        ruby
        type="button"
        :is-loading="uiFlags.isUpdating"
        @click="openDeletePopup"
      >
        {{ t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.BUTTON_TEXT') }}
      </NextButton>
    </div>

    <woot-confirm-delete-modal
      v-if="showDeleteConfirmation"
      v-model:show="showDeleteConfirmation"
      :title="t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.CONFIRM.TITLE')"
      :message="t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.CONFIRM.MESSAGE')"
      :confirm-text="
        t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.CONFIRM.BUTTON_TEXT')
      "
      :reject-text="
        t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.CONFIRM.DISMISS')
      "
      :confirm-value="accountName"
      :confirm-place-holder-text="
        t('GENERAL_SETTINGS.ACCOUNT_DELETE_SECTION.CONFIRM.PLACE_HOLDER', {
          accountName,
        })
      "
      @on-confirm="confirmDeletion"
      @on-close="closeDeletePopup"
    />
  </SectionLayout>
</template>
